import * as React from 'react';

import { CorporateEnrollmentModal } from './CorporateEnrollmentModal';
import {
  EnrollmentFlowStep,
  useCorporateEnrollmentFlow,
} from './useCorporateEnrollmentFlow';

interface CorporateEnrollmentFlowProps {
  /**
   * Open the flow automatically when the component mounts.
   */
  openOnMount?: boolean;

  /**
   * Called when user selects "No, I am not" from the corporate intro modal
   */
  onDeclineCorporateOption?: () => void;

  /**
   * Called when user selects "Create New Organization"
   */
  onCreateNewOrganization?: () => void;
  onClose?: () => void;

  /**
   * Render function for the "Request access list" step
   * Shows list of organizations to request access from
   */
  renderRequestAccessList?: (props: {
    onBack: () => void;
    onSelectOrganization: (organizationId: string) => void;
    onClose: () => void;
  }) => React.ReactNode;

  /**
   * Render function for the "Request access form" step
   * Shows the form to request access to a specific organization
   */
  renderRequestAccessForm?: (props: {
    organizationId: string;
    onBack: () => void;
    onSubmit: (data: any) => void;
    onClose: () => void;
  }) => React.ReactNode;

  /**
   * Render function for the "Request submitted" step
   * Shows confirmation that the request was submitted
   */
  renderRequestSubmitted?: (props: {
    onClose: () => void;
    onViewStatus?: () => void;
  }) => React.ReactNode;

  /**
   * Render function for the "Review request" step
   * Shows the submitted request details for review
   */
  renderReviewRequest?: (props: {
    onBack: () => void;
    onConfirm?: () => void;
    onError?: () => void;
    onClose: () => void;
  }) => React.ReactNode;

  /**
   * Render function for the "Request failed" step
   * Shows error if the request submission fails
   */
  renderRequestFailed?: (props: {
    onClose: () => void;
    onRetry?: () => void;
    onRestart?: () => void;
  }) => React.ReactNode;
}

export const CorporateEnrollmentFlow: React.FC<
  CorporateEnrollmentFlowProps
> = ({
  openOnMount,
  onDeclineCorporateOption,
  onCreateNewOrganization,
  onClose,
  renderRequestAccessList,
  renderRequestAccessForm,
  renderRequestSubmitted,
  renderReviewRequest,
  renderRequestFailed,
}) => {
  const flow = useCorporateEnrollmentFlow();

  React.useEffect(() => {
    if (openOnMount) {
      flow.open();
    }
  }, [openOnMount]);

  const handleCloseFlow = React.useCallback(() => {
    flow.close();
    onClose?.();
  }, [flow.close, onClose]);

  const handleNoClick = () => {
    handleCloseFlow();
    onDeclineCorporateOption?.();
  };

  const handleCreateNew = () => {
    handleCloseFlow();
    onCreateNewOrganization?.();
  };

  const handleYesClick = () => {
    flow.goToRequestAccessList();
  };

  const handleBackFromReview = React.useCallback(() => {
    if (flow.selectedOrganizationId) {
      flow.goToRequestAccessForm(flow.selectedOrganizationId);
    }
  }, [flow.goToRequestAccessForm, flow.selectedOrganizationId]);

  return (
    <>
      {/* Step 1: Corporate Enrollment Intro */}
      <CorporateEnrollmentModal
        isOpen={flow.currentStep === EnrollmentFlowStep.CORPORATE_INTRO}
        onClose={flow.close}
        onYesClick={handleYesClick}
        onNoClick={handleNoClick}
      />

      {/* Step 3: Request Access List of Organizations */}
      {flow.currentStep === EnrollmentFlowStep.REQUEST_ACCESS_LIST &&
        renderRequestAccessList?.({
          onBack: flow.goToCorporateIntro,
          onSelectOrganization: flow.goToRequestAccessForm,
          onClose: handleCloseFlow,
        })}

      {/* Step 4: Request Access Form */}
      {flow.currentStep === EnrollmentFlowStep.REQUEST_ACCESS_FORM &&
        flow.selectedOrganizationId &&
        renderRequestAccessForm?.({
          organizationId: flow.selectedOrganizationId,
          onBack: flow.goToRequestAccessList,
          onSubmit: flow.goToReviewRequest,
          onClose: handleCloseFlow,
        })}

      {/* Step 5: Review Request form */}
      {flow.currentStep === EnrollmentFlowStep.REVIEW_REQUEST &&
        renderReviewRequest?.({
          onBack: handleBackFromReview,
          onConfirm: flow.goToRequestSubmitted,
          onError: flow.goToRequestFailed,
          onClose: handleCloseFlow,
        })}

      {/* Step 6: Request Submitted */}
      {flow.currentStep === EnrollmentFlowStep.REQUEST_SUBMITTED &&
        renderRequestSubmitted?.({
          onClose: handleCloseFlow,
          onViewStatus: flow.goToReviewRequest,
        })}

      {/* Step 7: Request Failed */}
      {flow.currentStep === EnrollmentFlowStep.REQUEST_FAILED &&
        renderRequestFailed?.({
          onClose: handleCloseFlow,
          onRetry: handleBackFromReview,
          onRestart: flow.goToCorporateIntro,
        })}
    </>
  );
};

export { useCorporateEnrollmentFlow, EnrollmentFlowStep };
